/**
 * WAF routing (Owl "decisive") — per-domain routing strategies + the zone-level
 * routing config. Service-scoped (path `/services/:service_id/waf/…`) + `?domain=` (the subdomain
 * FQDN). Strategies decide which CDN platform serves each geo; the zone config's
 * `data_feed` decides whether those strategies apply (static) or live measurement
 * does (dynamic).
 *
 * A strategy's `detail` is a profile-specific config map, forwarded verbatim by
 * the backend (no key renaming), so the FE reads the same keys it writes:
 *   - static_route     → { platform, geolocations:[{country,region?,city?,network?}] }
 *   - best_latency /
 *     best_availability → { max_rtt_avg_ms, min_avail_percentage,
 *                           max_updated_delay_seconds, min_data_points_num }
 *   - regional_latency  → { config:{ platforms:[{id,handicap}], last_check,
 *                           max_latency, min_avail }, geolocations:[…] }
 *   - time_based / request_based weighted → { config:{ platforms:[{id,weight,enforce}],
 *                           last_check, max_latency, min_avail }, geolocations:[…] }
 *                           (exactly two CDN platforms required)
 *   - multi_route / multi_route_v2 → opaque (edited as raw JSON in the UI).
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationOptions,
} from '@tanstack/react-query';
import { serviceApiV3, wafKeys, unwrap, unwrapList, type ListResult } from './_shared';

/** A strategy profile (type) from the catalog (`GET /waf/routing/strategy-profiles`). */
export interface WafStrategyProfile {
  id: number;
  /** e.g. static_route | best_latency | best_availability | regional_latency | multi_route. */
  name: string;
}

/** A configured routing strategy on a domain. */
export interface WafStrategy {
  id: number;
  optimization_id: number;
  profile_id: number;
  priority: number;
  name: string;
  lock: boolean;
  activation: boolean;
  /** Per-profile config (shape depends on the profile — see file header). */
  detail: Record<string, unknown>;
}

/** Create body (`providers.StrategyCreate`). */
export interface WafStrategyCreate {
  profile_id: number;
  name: string;
  detail?: Record<string, unknown>;
}

/** Partial update body (`providers.StrategyUpdate`) — `profile_id` is immutable. */
export interface WafStrategyUpdate {
  name?: string;
  detail?: Record<string, unknown>;
  activation?: boolean;
}

/**
 * A geolocation scope used inside static_route / regional_latency `detail`.
 * Every field is optional — the picker emits only the dimensions the user chose
 * (e.g. a lone `{ network }`, or a `{ region, city }` with no country).
 */
export interface WafGeolocation {
  country?: string;
  region?: string;
  city?: string;
  network?: string;
}

/** Zone routing config (`providers.ZoneConfig`). */
export interface WafZoneConfig {
  cname: string;
  ttl: number;
  use_region_optimize: boolean;
  cname_flatten: boolean;
  vsr: boolean;
  exp_cost: boolean;
  optimization_id: number;
  fallback: string[];
  /** platform_id → edge_domain. */
  selected_platforms: Record<string, string>;
  /** "static" = route by the configured strategies; "dynamic" = live measurement. */
  data_feed: string;
  /** platform_id → monitor_asset_id. */
  measure_point_switch: Record<string, string>;
}

/** Partial zone-config update (`providers.ZoneConfigUpdate`). */
export interface WafZoneConfigUpdate {
  data_feed?: string;
  measure_point_switch?: Record<string, string>;
  /** Toggle CNAME flattening on the zone. */
  cname_flatten?: boolean;
  /** Resolver TTL, seconds. */
  ttl?: number;
}

/** Data-feed modes. */
export const WAF_DATA_FEED = { static: 'static', dynamic: 'dynamic' } as const;

/**
 * Friendly display labels for the known strategy profiles, keyed by `profile_id`.
 * The catalog's raw `name` (e.g. `best_latency`) is the fallback for any profile
 * not listed here. Weighted profiles (8, 10) require exactly two CDN platforms.
 */
export const WAF_STRATEGY_LABELS: Record<number, string> = {
  1: 'Static Routing',
  6: 'Best latency',
  7: 'Best availability',
  8: 'Time-based Weighted',
  9: 'Regional latency routing',
  10: 'Request-based Weighted',
};

/** Resolve a profile's display label, falling back to the given name / `#id`. */
export const wafStrategyLabel = (profileId: number, fallback?: string) =>
  WAF_STRATEGY_LABELS[profileId] ?? fallback ?? `#${profileId}`;

/** The catalog of strategy profiles (types) available to a service. */
export function useWafStrategyProfiles(serviceId: string | undefined) {
  return useQuery<WafStrategyProfile[]>({
    queryKey: wafKeys.routingProfiles(serviceId ?? ''),
    enabled: !!serviceId,
    staleTime: 5 * 60_000,
    queryFn: async () =>
      unwrap<WafStrategyProfile[]>(
        await serviceApiV3.get(`/services/${serviceId}/waf/routing/strategy-profiles`),
      ) ?? [],
  });
}

/** List a domain's routing strategies. */
export function useWafStrategies(
  serviceId: string | undefined,
  domain: string | undefined,
  enabled = true,
) {
  return useQuery<WafStrategy[]>({
    queryKey: wafKeys.routingStrategies(serviceId ?? '', domain ?? ''),
    enabled: !!serviceId && !!domain && enabled,
    staleTime: 15_000,
    queryFn: async () =>
      unwrap<WafStrategy[]>(
        await serviceApiV3.get(`/services/${serviceId}/waf/routing/strategies`, {
          params: { domain },
        }),
      ) ?? [],
  });
}

/** A domain's zone routing config (incl. the data-feed mode). */
export function useWafZoneConfig(
  serviceId: string | undefined,
  domain: string | undefined,
  enabled = true,
) {
  return useQuery<WafZoneConfig>({
    queryKey: wafKeys.routingZoneConfig(serviceId ?? '', domain ?? ''),
    enabled: !!serviceId && !!domain && enabled,
    staleTime: 30_000,
    queryFn: async () =>
      unwrap<WafZoneConfig>(
        await serviceApiV3.get(`/services/${serviceId}/waf/routing/zone-config`, {
          params: { domain },
        }),
      ),
  });
}

/**
 * One row of the routing report ("route map" / decisive report): which CDN
 * platform / strategy serves a given geo + network. `strategy` /
 * `strategy_name` are null/empty when the route isn't strategy-driven (the
 * baseline default route). Geo codes are `"00"` (or empty) for "any".
 */
export interface WafRoutingEntry {
  platform_id: string;
  /** Routing strategy ID; null/absent when the route uses the default. */
  strategy?: number | null;
  /** Human strategy label; empty when default. */
  strategy_name?: string;
  country: string;
  region: string;
  city: string;
  network: string;
}

/**
 * Geo/network filter for the routing report. Filters cascade: `region` needs
 * `country`, `city` needs `region`; `network` is country-scoped. Empty fields
 * are dropped (any).
 */
export interface WafRoutingReportFilter {
  country?: string;
  region?: string;
  city?: string;
  network?: string;
}

/**
 * A domain's routing report ("route map") — the per geo/network routing
 * decisions, paginated. Filterable by country/region/city/network.
 * `GET /services/:service_id/waf/routing?domain=&country=&region=&city=&network=&page=&page_size=`
 */
export function useWafRoutingReport(
  serviceId: string | undefined,
  domain: string | undefined,
  filter: WafRoutingReportFilter,
  page = 1,
  pageSize = 20,
) {
  const params = {
    domain,
    country: filter.country || undefined,
    region: filter.region || undefined,
    city: filter.city || undefined,
    network: filter.network || undefined,
    page,
    page_size: pageSize,
  };
  return useQuery<ListResult<WafRoutingEntry>>({
    queryKey: wafKeys.routingReport(serviceId ?? '', domain ?? '', params),
    enabled: !!serviceId && !!domain,
    staleTime: 15_000,
    queryFn: async () =>
      unwrapList<WafRoutingEntry>(await serviceApiV3.get(`/services/${serviceId}/waf/routing`, { params })),
  });
}

/** Create a routing strategy (`POST /waf/routing/strategies`). */
export function useCreateWafStrategy(
  serviceId: string | undefined,
  options?: UseMutationOptions<unknown, Error, { domain: string; input: WafStrategyCreate }>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ domain, input }: { domain: string; input: WafStrategyCreate }) =>
      unwrap<unknown>(
        await serviceApiV3.post(`/services/${serviceId}/waf/routing/strategies`, input, {
          params: { domain },
        }),
      ),
    ...options,
    onSuccess: (...args) => {
      if (serviceId) qc.invalidateQueries({ queryKey: ['waf', serviceId, 'routing-strategies'] });
      options?.onSuccess?.(...args);
    },
  });
}

/** Partial-update a routing strategy (`PATCH /waf/routing/strategies/:id`). */
export function useUpdateWafStrategy(
  serviceId: string | undefined,
  options?: UseMutationOptions<
    unknown,
    Error,
    { domain: string; id: number; input: WafStrategyUpdate }
  >,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ domain, id, input }: { domain: string; id: number; input: WafStrategyUpdate }) =>
      unwrap<unknown>(
        await serviceApiV3.patch(`/services/${serviceId}/waf/routing/strategies/${id}`, input, {
          params: { domain },
        }),
      ),
    ...options,
    onSuccess: (...args) => {
      if (serviceId) qc.invalidateQueries({ queryKey: ['waf', serviceId, 'routing-strategies'] });
      options?.onSuccess?.(...args);
    },
  });
}

/** Delete a routing strategy (`DELETE /waf/routing/strategies/:id`). */
export function useDeleteWafStrategy(
  serviceId: string | undefined,
  options?: UseMutationOptions<unknown, Error, { domain: string; id: number }>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ domain, id }: { domain: string; id: number }) =>
      unwrap<unknown>(
        await serviceApiV3.delete(`/services/${serviceId}/waf/routing/strategies/${id}`, {
          params: { domain },
        }),
      ),
    ...options,
    onSuccess: (...args) => {
      if (serviceId) qc.invalidateQueries({ queryKey: ['waf', serviceId, 'routing-strategies'] });
      options?.onSuccess?.(...args);
    },
  });
}

/** Update a domain's zone routing config (`PATCH /waf/routing/zone-config`). */
export function useUpdateWafZoneConfig(
  serviceId: string | undefined,
  options?: UseMutationOptions<WafZoneConfig, Error, { domain: string; input: WafZoneConfigUpdate }>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ domain, input }: { domain: string; input: WafZoneConfigUpdate }) =>
      unwrap<WafZoneConfig>(
        await serviceApiV3.patch(`/services/${serviceId}/waf/routing/zone-config`, input, {
          params: { domain },
        }),
      ),
    ...options,
    onSuccess: (...args) => {
      if (serviceId) qc.invalidateQueries({ queryKey: ['waf', serviceId, 'routing-zone-config'] });
      options?.onSuccess?.(...args);
    },
  });
}
